import { useState } from "react";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseStepper from "@/components_v2/Stepper/BaseStepper";
import BaseButton from "@/components_v2/Button/BaseButton";
import OrganizationTable from "@/domains/organizations/components/OrganizationTable";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import { useSendSurveyAssignmentsMutation } from "@/redux/service";
import { SurveyAudience } from "@/shared/utils/constants";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import UserTable from "@/features/users/list/components/UserTable";
import { SurveyInvitesModalProps } from "@/features/survey/management/types";

const SurveyInvitesModal = ({
  survey,
  open,
  setOpen,
}: SurveyInvitesModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedEntities, setSelectedEntities] = useState<
    BaseOrganizationResponse[]
  >([]);
  const [sendSurveyAssignments] = useSendSurveyAssignmentsMutation();
  const [isSubmissionRequired, setIsSubmissionRequired] = useState(false);

  const handleNext = () => {
    if (currentStep === 0) {
      if (selectedEntities.length === 0) {
        notification.error({
          message: t("survey.labels.selectOrganizationsFirst"),
        });
        return;
      }
      setCurrentStep(currentStep + 1);
    } else {
      sendInvites();
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentStep(0);
    setIsSubmissionRequired(false);
  };

  const sendInvites = () => {
    sendSurveyAssignments(
      selectedEntities.map((i) => ({
        surveyId: survey.id,
        ...(survey.audience === SurveyAudience.ORGANIZATION.value
          ? { organization: i.id }
          : { user: i.id }),
        isSubmissionRequired,
      })),
    )
      .unwrap()
      .then(() => {
        notification.success({
          message: t("survey.labels.sentSurveyInvitations"),
        });
        handleCancel();
      });
  };

  return (
    <BaseModal
      title={t("survey.labels.surveyInvites")}
      open={open}
      onCancel={handleCancel}
      width={1000}
      footer={
        <div className="modal-footer-buttons">
          <BaseButton variant="tertiary" onClick={handleCancel}>
            {t("survey.labels.cancel")}
          </BaseButton>

          <div className="flex gap-200">
            {currentStep === 1 && (
              <BaseButton variant="secondary" onClick={() => setCurrentStep(0)}>
                {t("survey.labels.back")}
              </BaseButton>
            )}
            <BaseButton onClick={handleNext}>
              {currentStep === 0
                ? t("survey.labels.next")
                : t("survey.labels.confirm")}
            </BaseButton>
          </div>
        </div>
      }
    >
      <BaseStepper
        current={currentStep}
        items={[
          {
            title: t("survey.labels.selectOrganizations"),
          },
          {
            title: t("survey.labels.confirmSelection"),
          },
        ]}
      />
      {currentStep === 0 &&
        (survey.audience === SurveyAudience.ORGANIZATION.value ? (
          <OrganizationTable
            columnNames={[
              "name",
              "country",
              "chapter",
              "communicatingStatus",
              "communityRoles",
            ]}
            filterNames={[
              "search",
              "countries",
              "chapters",
              "communityRoles",
              "communicatingStatus",
            ]}
            rowSelection={{
              onSelect: setSelectedEntities,
              selectedRows: selectedEntities,
              enabled: true,
              multiple: true,
              hideSelectAll: false,
            }}
            defaultFilters={{
              excludeInvitedToSurvey: survey.id,
            }}
          />
        ) : (
          <UserTable
            columnNames={[
              "fullName",
              "email",
              "organization",
              "jobTitle",
              "communityRoles",
            ]}
            filterNames={[
              "search",
              "organizations",
              "countries",
              "achievements",
              "communityRoles",
              "department",
              "jobTitle",
            ]}
            defaultFilters={{
              excludeInvitedToSurvey: survey.id,
            }}
          />
        ))}
      {currentStep === 1 && (
        <div className="flex flex-col gap-200">
          <p className="body-m-regular">
            {t("survey.labels.chosenEntitiesCountConfirmation", {
              selectedEntitiesCount: selectedEntities.length,
              plural: selectedEntities.length === 1 ? "y" : "ies",
            })}
          </p>

          <BaseCheckbox
            label={t("survey.labels.isSubmissionRequiredForInvites")}
            checked={isSubmissionRequired}
            onChange={() => setIsSubmissionRequired((oldVal) => !oldVal)}
          />
        </div>
      )}
    </BaseModal>
  );
};

export default SurveyInvitesModal;
